import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import SonitechLoginScena from '../components/animazioni/sonitechLoginScena';
import { T_ATTESA, T_USCITA, useTempo } from '../components/animazioni/motore';
import { APP_VERSION } from '../version';
import { riduciMovimentoAttivo } from './loginIntro';
import './login.css';

/* La pagina d'accesso E' l'animazione del titolare: la scritta SONITECH si compone, si
   trasforma nel simbolo, e il simbolo si apre nel riquadro delle credenziali. Li' il tempo
   si ferma e aspetta. Alla pressione di ENTRA riprende, salta la compilazione finta della
   sua anteprima e fa l'uscita: il riquadro si allarga, svanisce, e compare l'applicazione.

   I campi e il tasto sono quelli VERI, disegnati nelle stesse coordinate della scena. */

// Coordinate della scena (1920x1080), le stesse del file del titolare.
const CAMPI = { larghezza: 440, sinistra: 960 - 220, utente: 378, password: 500 };
const TASTO = { cy: 760, w: 440, h: 76 };
const CIANO = '#00A8DD';

function Campo({ etichetta, y, opacita, dy, children }) {
  return (
    <div style={{
      position: 'absolute', left: CAMPI.sinistra, top: y + dy, width: CAMPI.larghezza,
      opacity: opacita, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <label style={{
        fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 18,
        letterSpacing: '0.24em', color: '#9A99AE',
      }}>{etichetta}</label>
      {children}
    </div>
  );
}

const stileInput = (attivo) => ({
  height: 68, boxSizing: 'border-box', borderRadius: 4, width: '100%',
  background: 'rgba(255,255,255,0.05)', border: `2px solid ${attivo ? CIANO : '#34334A'}`,
  padding: '0 22px', fontFamily: "'Montserrat', sans-serif", fontWeight: 500, fontSize: 26,
  color: '#F2F2F5', outline: 'none', caretColor: CIANO,
});

export default function LoginPage() {
  const { verifica, applica } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [fuoco, setFuoco] = useState('');
  const [esce, setEsce] = useState(false);     // credenziali accettate: parte l'uscita
  const datiRef = useRef(null);
  const userRef = useRef(null);

  // Chi chiede meno movimento non deve subire quindici secondi di animazione: per lui la
  // scena parte gia' col riquadro formato.
  const [ridotto] = useState(riduciMovimentoAttivo);

  const entra = useCallback(() => {
    if (datiRef.current) applica(datiRef.current);
  }, [applica]);

  const T = useTempo({ fermaA: T_ATTESA, riprendiDa: T_USCITA, corri: esce, onFine: entra });
  const tempoScena = ridotto ? Math.max(T, T_ATTESA) : T;

  // Il cursore nel primo campo appena il riquadro e' pronto.
  const prontoRef = useRef(false);
  useEffect(() => {
    if (prontoRef.current || tempoScena < T_ATTESA - 0.05) return;
    prontoRef.current = true;
    if (userRef.current) userRef.current.focus({ preventScroll: true });
  }, [tempoScena]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busy || esce || !username.trim() || !password) return;
    setErr('');
    setBusy(true);
    try {
      datiRef.current = await verifica(username.trim(), password);
      setEsce(true);                 // da qui l'animazione fa il resto, poi entra()
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Accesso non riuscito');
      setBusy(false);
    }
  };

  // I campi veri, consegnati alla scena: li posiziona lei e li fa entrare col suo ritmo.
  const form = ({ st }) => (
    <form onSubmit={submit}>
      <Campo etichetta="UTENTE" y={CAMPI.utente} opacita={st(1)} dy={(1 - st(1)) * 24}>
        <input
          ref={userRef}
          style={stileInput(fuoco === 'u')}
          value={username}
          onChange={(ev) => setUsername(ev.target.value)}
          onFocus={() => setFuoco('u')}
          onBlur={() => setFuoco('')}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          disabled={esce}
        />
      </Campo>
      <Campo etichetta="PASSWORD" y={CAMPI.password} opacita={st(2)} dy={(1 - st(2)) * 24}>
        <input
          style={stileInput(fuoco === 'p')}
          type="password"
          value={password}
          onChange={(ev) => setPassword(ev.target.value)}
          onFocus={() => setFuoco('p')}
          onBlur={() => setFuoco('')}
          autoComplete="current-password"
          disabled={esce}
        />
      </Campo>

      {/* Il tasto ENTRA lo DISEGNA la scena (e' la barra azzurra che vi si trasforma):
          qui sopra ci sta solo la zona sensibile al clic, trasparente. */}
      <button
        type="submit"
        aria-label="Entra"
        disabled={busy || esce || !username.trim() || !password}
        style={{
          position: 'absolute', left: 960 - TASTO.w / 2, top: TASTO.cy - TASTO.h / 2,
          width: TASTO.w, height: TASTO.h, background: 'transparent', border: 'none',
          cursor: busy || esce ? 'default' : 'pointer', padding: 0,
        }}
      />

      {err && (
        <div role="alert" style={{
          position: 'absolute', left: CAMPI.sinistra, top: TASTO.cy + 60, width: CAMPI.larghezza,
          textAlign: 'center', fontFamily: "'Montserrat', sans-serif", fontSize: 19,
          color: '#ff8a80', letterSpacing: '0.04em',
        }}>{err}</div>
      )}

      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 34, textAlign: 'center',
        fontFamily: "'Montserrat', sans-serif", fontSize: 15, letterSpacing: '0.12em',
        color: 'rgba(255,255,255,0.28)',
      }}>versione {APP_VERSION}</div>
    </form>
  );

  return (
    <div className="soni-login-scena">
      <SonitechLoginScena T={tempoScena} form={form} />
    </div>
  );
}
